/* @flow */

import { createSelector } from '@reduxjs/toolkit';
import {
  hydrateMembership,
  getMembershipTimezone as backpackGetMembershipTimezone,
} from '@braindate/domain/lib/membership/util';
import {
  getAllLocations,
  getSelfLocations,
} from 'src/shared/domain/location/selector/locationSelectors';
import {
  getLocationTimezone,
  isVirtualLocation,
  isOnsiteLocation,
} from '@braindate/domain/lib/location/util';

const getMembershipId = (state) => state.app.membership;
const getEntities = (state) => state.domain.entities;

import type { Membership } from '@braindate/domain/lib/membership/type';
import type { State } from 'src/shared/core/type/reduxTypes';
import type { Location } from '@braindate/domain/lib/location/type';

export const getMembership: (state: State) => ?Membership = createSelector(
  [getMembershipId, getEntities],
  (id, entities) => {
    if (id) {
      return hydrateMembership(id, entities);
    }
  },
);

/**
 * Detect if we must use the membership timezone
 * @param {Array<Location>} eventLocations List of event Locations
 * @param {Array<Location>} userLocations List of user Locations
 * @returns {Boolean} True or false
 */
function isUsingMembershipTimezone(
  eventLocations: Array<Location>,
  userLocations: Array<Location>,
): boolean {
  if (eventLocations.length === 0 && userLocations.length === 0) return true;
  if (eventLocations.length === 1) {
    const [location] = eventLocations;
    if (isVirtualLocation(location)) {
      return true;
    }
  }

  if (userLocations.length === 1) {
    const [location] = userLocations;
    if (isVirtualLocation(location)) {
      return true;
    }
  }
  return false;
}

/**
 * Return the must use location in a list.
 * If there are multiple locations, it will return the first onsite
 *
 * @param {Array<Location>} locations List of locations
 * @returns {Location} Return the location
 */
function getSingleLocation(locations: Array<Location>): ?Location {
  if (locations.length === 1) {
    return locations[0];
  }
  return locations.find(isOnsiteLocation);
}

/**
 * Return the must use location from User and event Location
 * Will always check user locations and fallback to event locations
 * @param {Array<Location>} eventLocations List of event Locations
 * @param {Array<Location>} userLocations List of user Locations
 * @returns {Location} Return the location
 */
function findLocationForTimezone(
  eventLocations: Array<Location>,
  userLocations: Array<Location>,
): ?Location {
  const userLocation = getSingleLocation(userLocations);
  if (userLocation) return userLocation;

  const eventLocation = getSingleLocation(eventLocations);
  return eventLocation;
}

export function computeMembershipTimezone(
  membership: ?Membership,
  eventLocations: Array<Location>,
  userLocations: Array<Location>,
): ?string {
  if (isUsingMembershipTimezone(eventLocations, userLocations)) {
    const membershipTimezone =
      membership && backpackGetMembershipTimezone(membership);
    if (membershipTimezone) return membershipTimezone;
  }

  const location = findLocationForTimezone(eventLocations, userLocations);
  return location ? getLocationTimezone(location) : null;
}

export const getMembershipTimezone: (state: State) => ?Membership =
  createSelector(
    getMembership,
    getAllLocations,
    getSelfLocations,
    (
      membership: ?Membership,
      eventLocations: Array<Location>,
      userLocations: Array<Location>,
    ) => computeMembershipTimezone(membership, eventLocations, userLocations),
  );
